// oxlint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Component } from 'react';
import {
  AUDIO_EXTENSIONS,
  canPlay,
  DASH_EXTENSIONS,
  FLV_EXTENSIONS,
  HLS_EXTENSIONS
} from '../../../constants';
import { defaultProps } from '../props';
import type { BaseReactPlayerProps } from '../types';
import { getSDK, isMediaStream } from '../utils';

const HAS_NAVIGATOR = typeof navigator !== 'undefined';
const IS_ANDROID =
  HAS_NAVIGATOR && navigator.userAgent.toLowerCase().includes('android');
const IS_IPAD_PRO =
  HAS_NAVIGATOR &&
  navigator.platform === 'MacIntel' &&
  navigator.maxTouchPoints > 1;
const IS_IOS =
  HAS_NAVIGATOR &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || IS_IPAD_PRO) &&
  !window.MSStream;
const IS_SAFARI =
  HAS_NAVIGATOR &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
  !window.MSStream;
const HLS_SDK_URL =
  'https://cdn.jsdelivr.net/npm/hls.js@VERSION/dist/hls.min.js';
const HLS_GLOBAL = 'Hls';
const DASH_SDK_URL =
  'https://cdnjs.cloudflare.com/ajax/libs/dashjs/VERSION/dash.all.min.js';
const DASH_GLOBAL = 'dashjs';
const FLV_SDK_URL =
  'https://cdn.jsdelivr.net/npm/flv.js@VERSION/dist/flv.min.js';
const FLV_GLOBAL = 'flvjs';
const MATCH_DROPBOX_URL = /www\.dropbox\.com\/.+/;
const MATCH_CLOUDFLARE_STREAM =
  /https:\/\/watch\.cloudflarestream\.com\/([a-z0-9]+)/;
const REPLACE_CLOUDFLARE_STREAM =
  'https://videodelivery.net/{id}/manifest/video.m3u8';
const SHOULD_SKIP_RECONNECT = IS_IOS || IS_SAFARI;

function supportsWebKitPresentationMode(
  video = document.createElement('video')
) {
  // Check if Safari supports PiP, and is not on mobile (other than iPad)
  // iPhone safari appears to "support" PiP through the check, however PiP does not function
  return (
    video.webkitSupportsPresentationMode &&
    typeof video.webkitSetPresentationMode === 'function' &&
    !/iPhone|iPod/.test(navigator.userAgent)
  );
}

function canEnablePIP(url) {
  return (
    canPlay(url) &&
    (!!document.pictureInPictureEnabled || supportsWebKitPresentationMode()) &&
    !AUDIO_EXTENSIONS.test(url)
  );
}

export class FilePlayer extends Component<BaseReactPlayerProps> {
  static defaultProps = defaultProps;

  static canPlay = canPlay.file;

  static canEnablePIP = canEnablePIP;

  stallTimer: number | null = null;
  reconnectTimer: number | null = null;
  retryCount = 0;
  isReconnecting = false;
  hasUserInteracted = false;
  shouldFixAndroidAutoplay = IS_ANDROID && this.props.playing;
  readonly STALL_TIMEOUT_MS = 5_000;
  readonly MAX_RETRIES = 6;
  readonly MAX_RETRY_DELAY_MS = 30_000;
  readonly RETRY_BASE_DELAY_MS = 1_000;

  componentDidMount() {
    this.props.onMount?.(this);

    this.addListeners(this.player);
    const src = this.getSource(this.props.url); // Ensure src is set in strict mode
    if (src) {
      this.player.src = src;
    }
    if (IS_IOS || this.props.config.forceDisableHls) {
      this.player.load();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.shouldUseAudio(this.props) !== this.shouldUseAudio(prevProps)) {
      this.removeListeners(this.prevPlayer);
      this.addListeners(this.player);
    }
  }

  componentWillUnmount() {
    this.player.removeAttribute('src');
    this.removeListeners(this.player);
    this.hls?.destroy();
    this.cleanupTimers();
  }

  addListeners(player) {
    const { playsinline } = this.props;

    player.addEventListener('canplay', this.onReady, { once: true });
    player.addEventListener('play', this.onPlay);
    player.addEventListener('waiting', this.onBuffer);
    player.addEventListener('playing', this.onBufferEnd);
    player.addEventListener('pause', this.onPause);
    player.addEventListener('seeked', this.onSeek);
    player.addEventListener('stalled', this.onStallStart);
    player.addEventListener('ended', this.onEnded);
    player.addEventListener('error', this.onError);
    player.addEventListener('enterpictureinpicture', this.onEnablePIP);
    player.addEventListener('leavepictureinpicture', this.onDisablePIP);
    player.addEventListener(
      'webkitpresentationmodechanged',
      this.onPresentationModeChange
    );

    if (playsinline) {
      player.setAttribute('playsinline', '');
      player.setAttribute('webkit-playsinline', '');
      player.setAttribute('x5-playsinline', '');
    }

    if (this.shouldFixAndroidAutoplay) {
      document.addEventListener(
        'pointerup',
        this.handleFirstUserInteractionAndroid
      );
    }
  }

  removeListeners(player) {
    player.removeEventListener('canplay', this.onReady);
    player.removeEventListener('play', this.onPlay);
    player.removeEventListener('waiting', this.onBuffer);
    player.removeEventListener('stalled', this.onStallStart);
    player.removeEventListener('playing', this.onBufferEnd);
    player.removeEventListener('pause', this.onPause);
    player.removeEventListener('seeked', this.onSeek);
    player.removeEventListener('ended', this.onEnded);
    player.removeEventListener('error', this.onError);
    player.removeEventListener('enterpictureinpicture', this.onEnablePIP);
    player.removeEventListener('leavepictureinpicture', this.onDisablePIP);
    player.removeEventListener(
      'webkitpresentationmodechanged',
      this.onPresentationModeChange
    );

    if (this.shouldFixAndroidAutoplay) {
      document.removeEventListener(
        'pointerup',
        this.handleFirstUserInteractionAndroid
      );
    }
  }

  onStallStart = () => {
    if (
      SHOULD_SKIP_RECONNECT ||
      this.stallTimer != null ||
      this.isReconnecting
    ) {
      return;
    }

    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      this.onError(new Error('Stream stalled for too long'));
      this.player.pause();
    }, this.STALL_TIMEOUT_MS);
  };

  handleStallEnd = () => {
    if (this.stallTimer != null) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  };

  reconnectStream() {
    if (!this.canReconnect()) {
      return;
    }

    this.prepareReconnect();

    const delay = Math.min(
      this.MAX_RETRY_DELAY_MS,
      2 ** this.retryCount * this.RETRY_BASE_DELAY_MS
    );

    this.scheduleReconnect(delay);
  }

  private canReconnect() {
    if (this.isReconnecting || this.retryCount >= this.MAX_RETRIES) {
      console.warn('Max reconnection attempts reached');
      return false;
    }
    return true;
  }

  private prepareReconnect() {
    this.isReconnecting = true;
    this.cleanupTimers();
    this.retryCount++;
    this.player.pause();
    this.player.removeAttribute('src');
  }

  private scheduleReconnect(delay) {
    const { url, playing } = this.props;
    this.reconnectTimer = setTimeout(() => {
      try {
        let src = this.getSource(url);

        if (!src) {
          return;
        }

        this.player.src = src;
        this.player.load();

        if (playing) {
          this.player.play().catch((e) => {
            this.onError(e);
          });
        }
      } catch (e) {
        this.onError(e);
      } finally {
        this.isReconnecting = false;
      }
    }, delay);
  }

  private cleanupTimers() {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  handleFirstUserInteractionAndroid = () => {
    if (this.hasUserInteracted) {
      return;
    }

    this.hasUserInteracted = true;
    this.unmute();
    const playPromise = this.play();

    if (!playPromise) {
      return;
    }

    playPromise.catch((e) => {
      console.warn('Play after unmute failed:', e);
      this.props.onError?.(e);
    });

    document.removeEventListener(
      'pointerup',
      this.handleFirstUserInteractionAndroid
    );
  };

  // Proxy methods to prevent listener leaks
  onReady = (...args) => {
    const { onReady } = this.props;
    onReady(...args);
  };

  onPlay = (...args) => {
    const { onPlay } = this.props;
    onPlay(...args);
    this.retryCount = 0;
    this.handleStallEnd();
  };

  onBuffer = (...args) => {
    const { onBuffer } = this.props;
    onBuffer(...args);
  };

  onBufferEnd = (...args) => {
    const { onBufferEnd } = this.props;
    onBufferEnd(...args);
  };

  onPause = (...args) => {
    const { onPause } = this.props;
    onPause(...args);
  };

  onEnded = (...args) => {
    const { onEnded, loop } = this.props;
    onEnded(...args);
    if (loop) {
      this.play();
    }
  };

  onError = (...args) => {
    const [error] = args;
    const shouldReconnect = this.shouldAttemptReconnect(error);

    if (shouldReconnect) {
      this.reconnectStream();
    }

    const { onError } = this.props;
    onError(...args);
  };

  shouldAttemptReconnect(error: unknown) {
    if (SHOULD_SKIP_RECONNECT) {
      return false;
    }

    if (
      error instanceof Error &&
      error.message === 'Stream stalled for too long'
    ) {
      return true;
    }

    if (error instanceof Event) {
      const target = error.target as HTMLMediaElement;
      const mediaError = target?.error;

      return (
        mediaError?.code === MediaError.MEDIA_ERR_NETWORK ||
        mediaError?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
      );
    }

    return false;
  }

  onEnablePIP = (...args) => {
    const { onEnablePIP } = this.props;

    onEnablePIP(...args);
  };

  onDisablePIP = (e) => {
    const { onDisablePIP, playing } = this.props;

    onDisablePIP(e);

    if (playing) {
      this.play();
    }
  };

  onPresentationModeChange = (e) => {
    if (this.player && supportsWebKitPresentationMode(this.player)) {
      const { webkitPresentationMode } = this.player;
      if (webkitPresentationMode === 'picture-in-picture') {
        this.onEnablePIP(e);
      } else if (webkitPresentationMode === 'inline') {
        this.onDisablePIP(e);
      }
    }
  };

  onSeek = (e) => {
    const { onSeek } = this.props;

    onSeek(e.target.currentTime);
  };

  shouldUseAudio(props) {
    if (props.config.file.forceVideo) {
      return false;
    }
    if (props.config.file.attributes.poster) {
      return false; // Use <video> so that poster is shown
    }
    return AUDIO_EXTENSIONS.test(props.url) || props.config.file.forceAudio;
  }

  shouldUseHLS(url) {
    if (
      (IS_SAFARI && this.props.config.forceSafariHLS) ||
      this.props.config.forceHLS
    ) {
      return true;
    }
    if (IS_IOS || this.props.config.forceDisableHls) {
      return false;
    }
    return HLS_EXTENSIONS.test(url) || MATCH_CLOUDFLARE_STREAM.test(url);
  }

  shouldUseDASH(url) {
    return DASH_EXTENSIONS.test(url) || this.props.config.file.forceDASH;
  }

  shouldUseFLV(url) {
    return FLV_EXTENSIONS.test(url) || this.props.config.forceFLV;
  }

  load(url, isReady) {
    const {
      config: { file }
    } = this.props;
    const { hlsVersion, hlsOptions, dashVersion, flvVersion } = file;

    this.hls?.destroy();
    this.dash?.reset();

    if (this.shouldUseHLS(url)) {
      getSDK(HLS_SDK_URL.replace('VERSION', hlsVersion), HLS_GLOBAL).then(
        (Hls) => {
          this.hls = new Hls(hlsOptions);
          this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
            this.props.onReady();
          });
          this.hls.on(Hls.Events.ERROR, (e, data) => {
            this.props.onError(e, data, this.hls, Hls);
          });
          if (MATCH_CLOUDFLARE_STREAM.test(url)) {
            const id = url.match(MATCH_CLOUDFLARE_STREAM)[1];
            this.hls.loadSource(REPLACE_CLOUDFLARE_STREAM.replace('{id}', id));
          } else {
            this.hls.loadSource(url);
          }
          this.hls.attachMedia(this.player);
          this.props.onLoaded();
        }
      );
    }

    if (this.shouldUseDASH(url)) {
      getSDK(DASH_SDK_URL.replace('VERSION', dashVersion), DASH_GLOBAL).then(
        (dashjs) => {
          this.dash = dashjs.MediaPlayer().create();
          this.dash.initialize(this.player, url, this.props.playing);
          this.dash.on('error', this.props.onError);
          if (parseInt(dashVersion) < 3) {
            this.dash.getDebug().setLogToBrowserConsole(false);
          } else {
            this.dash.updateSettings({
              debug: { logLevel: dashjs.Debug.LOG_LEVEL_NONE }
            });
          }
          this.props.onLoaded();
        }
      );
    }

    if (this.shouldUseFLV(url)) {
      getSDK(FLV_SDK_URL.replace('VERSION', flvVersion), FLV_GLOBAL).then(
        (flvjs) => {
          this.flv = flvjs.createPlayer({ type: 'flv', url });
          this.flv.attachMediaElement(this.player);
          this.flv.on(flvjs.Events.ERROR, (e, data) => {
            this.props.onError(e, data, this.flv, flvjs);
          });
          this.flv.load();
          this.props.onLoaded();
        }
      );
    }

    if (url instanceof Array) {
      // When setting new urls (<source>) on an already loaded video,
      // HTMLMediaElement.load() is needed to reset the media element
      // and restart the media resource. Just replacing children source
      // dom nodes is not enough
      this.player.load();
    } else if (isMediaStream(url)) {
      try {
        this.player.srcObject = url;
      } catch (_e) {
        this.player.src = window.URL.createObjectURL(url);
      }
    }

    if (isReady) {
      this.props.onLoaded();
    }
  }

  play() {
    if (this.shouldFixAndroidAutoplay && !this.hasUserInteracted) {
      return;
    }

    const { onError, onTogglePlaying } = this.props;
    const promise = this.player.play();

    if (promise) {
      promise
        .then(() => onTogglePlaying(true))
        .catch((e) => {
          console.warn(e.message);
          onError(e);
        });
    }
  }

  pause() {
    this.player.pause();
  }

  stop() {
    this.player.removeAttribute('src');
    if (this.hls) {
      this.hls.destroy();
    }
    if (this.dash) {
      this.dash.reset();
    }
  }

  seekTo(seconds) {
    this.player.currentTime = seconds;
  }

  setVolume(fraction) {
    this.player.volume = fraction;
  }

  mute = () => {
    this.player.muted = true;
  };

  unmute = () => {
    this.player.muted = false;
  };

  enablePIP() {
    if (
      this.player.requestPictureInPicture &&
      document.pictureInPictureElement !== this.player
    ) {
      this.player.requestPictureInPicture();
    } else if (
      supportsWebKitPresentationMode(this.player) &&
      this.player.webkitPresentationMode !== 'picture-in-picture'
    ) {
      this.player.webkitSetPresentationMode('picture-in-picture');
    }
  }

  disablePIP() {
    if (
      document.exitPictureInPicture &&
      document.pictureInPictureElement === this.player
    ) {
      document.exitPictureInPicture();
    } else if (
      supportsWebKitPresentationMode(this.player) &&
      this.player.webkitPresentationMode !== 'inline'
    ) {
      this.player.webkitSetPresentationMode('inline');
    }
  }

  setPlaybackRate(rate) {
    this.player.playbackRate = rate;
  }

  getDuration() {
    if (!this.player) return null;

    const { duration, seekable } = this.player;

    // on iOS, live streams return Infinity for the duration
    // so instead we use the end of the seekable timerange
    if (duration === Infinity && seekable.length > 0) {
      return seekable.end(seekable.length - 1);
    }

    return duration;
  }

  getCurrentTime() {
    if (!this.player) return null;
    return this.player.currentTime;
  }

  getSecondsLoaded() {
    if (!this.player) return null;

    const { buffered } = this.player;
    if (buffered.length === 0) {
      return 0;
    }

    const end = buffered.end(buffered.length - 1);
    const duration = this.getDuration();
    if (end > duration) {
      return duration;
    }

    return end;
  }

  getSource(url) {
    const useHLS = this.shouldUseHLS(url);
    const useDASH = this.shouldUseDASH(url);

    if (url instanceof Array || isMediaStream(url) || useHLS || useDASH) {
      return undefined;
    }

    if (MATCH_DROPBOX_URL.test(url)) {
      return url.replace('dl=0', 'raw=1');
    }

    return url;
  }

  renderSourceElement = (source, index) => {
    if (typeof source === 'string') {
      return <source key={index} src={source} />;
    }
    return <source key={index} {...source} />;
  };

  renderTrack = (track, index) => <track key={index} {...track} />;

  ref = (player) => {
    if (this.player) {
      // Store previous player to be used by removeListeners()
      this.prevPlayer = this.player;
    }
    this.player = player;
  };

  render() {
    const { url, playing, controls, muted, config, width, height } = this.props;

    const useAudio = this.shouldUseAudio(this.props);
    const Element = useAudio ? 'audio' : 'video';
    const style = {
      width: width === 'auto' ? width : '100%',
      height: height === 'auto' ? height : '100%'
    };

    return (
      <Element
        ref={this.ref}
        src={this.getSource(url)}
        style={style}
        preload="auto"
        autoPlay={playing || undefined}
        controls={controls}
        muted={this.shouldFixAndroidAutoplay || muted}
        {...config.file.attributes}
      >
        {url instanceof Array && url.map(this.renderSourceElement)}
        {config.file.tracks.map(this.renderTrack)}
      </Element>
    );
  }
}
